import { VoyageRoute, VoyageRouteLineFeature, VoyageRoutePointFeature } from "./voyage-routing-model";

export type PlanChangedEvent = CustomEvent<{scheduleId: string}>

export interface PassagePlanDraft
{
    foreDraft: number|null;
    aftDraft: number|null;
    airDraft: number|null;
    swDensity: number|null;
}

export interface DraftsType 
{
    berthToPilot: PassagePlanDraft;
    pilotToPilot: PassagePlanDraft;
    pilotToBerth: PassagePlanDraft;
}

export interface PassagePlanSquat
{
    openWaters: number|null;
    confinedWaters: number|null;
}

export interface PassagePlanUKC
{
    required: string;
    minimumExpected: number|null;
}

export interface PassagePlanBridgeTeam
{
    master: boolean;
    chiefOfficer: boolean;
    secondOfficer: boolean;
    thirdOfficer: boolean;
    pilot: boolean;
    helmsman: boolean;
    lookout1: boolean;
    lookout2: boolean;
}

export type PlanStatus = "planning"|"approved";

export interface VoyagePlan
{
    imoNo?: number;
    source?: string;
    scheduleId: string;
    etd: number;
    eta: number;
    departureTerminalCode: string;
    startPosition: [number, number]|null;
    departurePilotPosition: [number, number]|null;
    departurePilotEtd: number;
    arrivalTerminalCode: string;
    endPosition: [number, number]|null;
    arrivalPilotPosition: [number, number]|null;
    arrivalPilotEta: number;
    foreDraft: number;
    aftDraft: number;
    gmt: number|null;
    numberOfReefers: number;
    status: PlanStatus;
    passagePlan: {
        description: string;
        departurePort: string;
        arrivalPort: string;
        portMemo: string;
        reminders: string;
        notEcdisCompliant: boolean;
        encCellsUsed: string;
        booksAndTables: string;
        chartsAndLightsNotes: string;
        sailingDirections: string;
        listOfRadioSignals: string;
        listOfLights: string;
        gpsEcdisRadarMap: string;
        paperCharts: string;
        pubCorrectedToWeek: string;
        tideTables: string;
        publicationsNotes: string;
        positionValidation: string;
        mastersOrders: string;
        drafts: DraftsType;
        squats: {
            minSpeed: PassagePlanSquat;
            maxSpeed: PassagePlanSquat;
            bufferSpeed: PassagePlanSquat;
            vesselMaxSpeed: PassagePlanSquat;
        };
        underKeelClearance: {
            confinedWater: PassagePlanUKC;
            openWater: PassagePlanUKC;
            alongsideDeparture: PassagePlanUKC;
            alongsideArrival: PassagePlanUKC;
        };
        safetyMessages: {
            navareaRegions: string;
            navtexStations: string;
        };
        weatherInfo: {
            navareaRegions: string;
            navtexStations: string;
            info: string;
        };
        equipmentStatus: {
            operationalCondition: string;
            failures: string;
        };
        bridgeTeam: {
            departure: PassagePlanBridgeTeam;
            arrival: PassagePlanBridgeTeam;
        };
        calculatedValues: {
            avgSpeedOGPilotToPilot: number|null;
            routeMinSpeed: number|null;
            routeMaxSpeed: number|null;
            routeAverageSpeed: number|null;
            berthToPilotDistance: number|null;
            pilotToPilotDistance: number|null;
            pilotToBerthDistance: number|null;
        };
        signatures: Signature[]
    };
    routes: VoyageRoute[];
    selectedRouteIndex: number;
    autoUpdateReason: string;
    create_dtm?: string;
    update_dtm?: string;
    metadata?: {
        speed?: number;
    }
    application_type?: string | null;
    primaryvoyageplanningtool?: number | null;
}

export interface Signature
{
    rank: string;
    name: string;
}

export interface VesselCharacteristics
{
    height: number;
    minSpeed: number; // in m/s
    maxSpeed: number; // in m/s
    blockCoefficient: number;
    ecdisWPStartIndex: number;
}

export abstract class VesselPlanningModelBase extends EventTarget
{
    abstract getPlans(scheduleId: string): Promise<VoyagePlan[]>;
    abstract getCurrentPlan(scheduleId: string): Promise<VoyagePlan|null>;
    abstract getApprovedPlan(scheduleId: string): Promise<VoyagePlan|null>;
    abstract getAllPlansStatus(): Promise<{[index:string]: PlanStatus}>;
    abstract getExecutingPlan(): Promise<VoyagePlan|null>;
    abstract hasPlan(scheduleId: string): Promise<boolean>;
    abstract createPlan(scheduleId: string): Promise<VoyagePlan>;
    abstract setPlan(scheduleId: string, plan: VoyagePlan): Promise<void>;
    abstract calculatePlan(plan: VoyagePlan): Promise<VoyagePlan>;
    abstract approvePlan(scheduleId: string): Promise<void>;

    abstract getVesselCharacteristics(): Promise<VesselCharacteristics|null>;
    abstract setVesselCharacteristics(characteristics: VesselCharacteristics): Promise<void>;

    abstract passagePlanChanged(oldPlan: VoyagePlan, newPlan: VoyagePlan): boolean;
    abstract passagePlanRouteChanged(oldRoute: Array<VoyageRouteLineFeature|VoyageRoutePointFeature>,
                                     newRoute: Array<VoyageRouteLineFeature|VoyageRoutePointFeature>): boolean;

    abstract approvalReminder(message: string, autoApprove?: boolean): void;
}

let VesselPlanningModel: VesselPlanningModelBase|null = null;

function setVesselPlanningModel(model: VesselPlanningModelBase)
{
    VesselPlanningModel = model;
}

export { VesselPlanningModel, setVesselPlanningModel };
